import {Component, Input, TemplateRef} from '@angular/core';
import {SearchService} from "../shared/search.service";
import {SearchDescription} from "../interfaces/searchDescription";
import {ColDef, ColType} from "../interfaces/colDef";
import {FormBuilder, FormGroup} from "@angular/forms";
import {BSValidators} from "../shared/bsvalidators";
import {Subject, Subscription} from "rxjs";
import {SubscribedComponent} from "../subscribed.component";
import {NbDialogRef, NbLayoutScrollService, NbScrollStrategies} from "@nebular/theme";
import {Searchable} from "../interfaces/user";
import _ from 'lodash';
import moment from 'moment';
import {Globals} from "../shared/Globals";

@Component({
    selector: 'bst-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss'],
    standalone: false
})
export class SearchComponent<T extends Searchable> extends SubscribedComponent{

  public appending: boolean = false;
  public readonly scrollStrategy: NbScrollStrategies = 'block';

  private def: SearchDescription = null;
  public ColType = ColType;
  public form: FormGroup;
  private res: T[] = [];
  public readonly selected = new Subject<T>();
  public scrolling: boolean = true;
  public selections: T[] = [];
  public params = null;
  private page: number = 1;
  private valchange: Subscription = null;

  private readonly dsearch = _.debounce(() => {
    this.search();
  }, 800);

  @Input() public target: string;
  @Input() public describe: string = null;
  @Input() public options: object = null;
  @Input() public hidden: (T|number)[] = [];
  @Input() public autosearch: boolean = false;
  @Input() public prefill: boolean = false;
  @Input() public filters: object = {};

  @Input() public headerTemplate: TemplateRef<any>;
  @Input() public bodyTemplate: TemplateRef<any>;
  @Input() public selectionHeaderTemplate: TemplateRef<any>;
  @Input() public selectionBodyTemplate: TemplateRef<any>;
  @Input() public summaryTemplate: TemplateRef<any>;
  @Input() public buttonTemplate: TemplateRef<any> = null;

  constructor(private searchService: SearchService,
              private ref: NbDialogRef<SearchComponent<T>>,
              public scroll: NbLayoutScrollService,
              private fb: FormBuilder) {
    super();

  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if(this.valchange){
      this.valchange.unsubscribe();
      this.valchange = null;
    }
  }

  public get definition(){
    return this.def;
  }

  public get topDefs(){
    return this.def.colDefs.filter((o) => {
      return o.display === 'top';
    });
  }

  public get sideDefs(){
    return this.def.colDefs.filter((o) => {
      return o.display !== 'top';
    });
  }

  public isSelected(o: T){
    return this.selections.findIndex((m) => {
      return m.id === o.id
    }) !== -1;
  }

  public isHidden(o: T){
    return this.hidden.find((m) => {
      if(typeof m === 'number'){
        return m === o.id;
      }
      else{
        return <unknown>m.id === o.id;
      }
    });
  }

  public get results(){
    return this.res.filter((o) => {
      return !this.isSelected(o) && !this.isHidden(o);
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.loading.next(true);
    if(this.target) {

      this.subs.push(this.scroll
        .onScrollableChange()
        .subscribe((scrollable: boolean) => {
          this.scrolling = scrollable;
        }));

      this.subs.push(this.selected.subscribe((o: T) => {
        if(!this.isSelected(o)){
          this.selections.push(o);
        }
        else{
          this.selections = this.selections.filter((o1) => {
            return o1.id !== o.id;
          });

          if(this.res.findIndex((o1) => {
            return o1.id === o.id
          }) === -1){
            this.res.push(o);
          }
        }
      }));

      this.searchService.getDescription(this.describe ?? this.target, this.options).then((def: SearchDescription) => {
        this.def = def;
        this.loading.next(false);
        this.reset();
      });
    }
    else{
      this.loading.next(false);
      console.error('no target for search');
    }
  }

  private default(col: ColDef){
    let def = col.default || null;
    if(typeof def !== null){
      if(col.type === ColType.RANGE){
        def = {
          start: Globals.fromDateString(def.start),
          end: Globals.fromDateString(def.end)
        };
      }
      else if(col.type === ColType.DATE){
        def = Globals.fromDateString(def);
      }
    }
    return def;
  }

  public reset(){
    if(this.def) {
      this.res = [];

      const group = {};
      if(!this.form){
        this.def.colDefs.forEach((col) => {
          group[col.name] = [this.default(col), (col.required ? [BSValidators.required] : [])];
        });
        this.form = this.fb.group(group);
      }
      else{
        this.def.colDefs.forEach((col) => {
          group[col.name] = this.default(col);
        });

        this.form.setValue(group);

        if(this.valchange){
          this.valchange.unsubscribe();
          this.valchange = null;
        }
      }

      if(this.autosearch) {
        if(this.valchange){
          this.valchange.unsubscribe();
          this.valchange = null;
        }

        setTimeout(() => {
          this.valchange = this.form.valueChanges.subscribe(this.dsearch);
          if(this.prefill) {
            this.search();
          }
        });
      }
      else if(this.prefill){
        this.search();
      }
    }
  }

  public async search() {
    if(!this.isLoading) {
      this.loading.next(true)
      this.params = _.cloneDeep({...this.form.getRawValue(), ...this.filters});

      // Stupid datetime conversion (remove time). The datepicker gives Moments, presets and settings give Dates.
      const isDay = (v: any) => v instanceof Date || moment.isMoment(v);
      for(let p in this.params){
        if(this.params.hasOwnProperty(p) && this.params[p]) {
          if(isDay(this.params[p].start) && isDay(this.params[p].end)){
            this.params[p].start = Globals.toISODate(this.params[p].start);
            this.params[p].end = Globals.toISODate(this.params[p].end);
          }
          else if(isDay(this.params[p])){
            this.params[p] = Globals.toISODate(this.params[p]);
          }
        }
      }

      this.page = 1;
      try {
        this.res = await this.searchService.search(this.target, this.params, null, this.page);
        console.log(this.res);
      } catch(error){
        console.error(error);
      }

      this.loading.next(false);
    }
  }

  public async scrolled(){
    if(!this.appending && this.params && this.target && this.page > 0){
      this.appending = true;

      try{
        const res = await this.searchService.search(this.target, this.params, null, this.page + 1);
        this.res = this.res.concat(res);

        if(res && res.length){
          this.page += 1;
        }
        else{
          this.page = 0;
        }
      } catch (error){
        console.error(error);
      }

      this.appending = false;
    }
  }

  finish(otherdata: any = null) {
    this.ref.close({
      selections: this.selections,
      params: this.params,
      data: otherdata
    });
  }

  cancel(){
    this.ref.close(false);
  }
}
